import { useState } from 'react';
import {
  changeTenantAdminEmail,
  resendTenantInvitation,
  sendTenantAdminPasswordReset,
  type TenantAdmin,
} from '@/api/superadmin';
import { Button } from '@/components/primitives/button';
import { CopyButton } from '@/components/primitives/copy-button';
import { Input } from '@/components/primitives/input';

interface Props {
  organizationId: string;
  admin: TenantAdmin | null;
  onChanged: () => void;
}

const STATUS_LABEL: Record<string, string> = {
  active: 'Active',
  invited: 'Invited — has not set a password yet',
  invite_expired: 'Invitation expired',
};

/**
 * The tenant's administrator, and the three things an operator can do about it.
 *
 * Every action is disabled when there is no resolvable admin: organizations
 * that predate this console may have lost their memberships, and the API
 * refuses those with a 409 rather than guessing at a user to act on.
 */
export function TenantAdminCard({ organizationId, admin, onChanged }: Props) {
  const [open, setOpen] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [fallbackLink, setFallbackLink] = useState<string | null>(null);

  const run = async (name: string, action: () => Promise<void>) => {
    setBusy(name);
    setError(null);
    setNotice(null);
    setFallbackLink(null);

    try {
      await action();
    } catch (caught) {
      setError((caught as Error).message);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="border-stroke-soft bg-bg-white flex flex-col gap-4 rounded-xl border p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex flex-col gap-1">
          <h3 className="text-label-sm text-text-strong">Tenant administrator</h3>
          {admin ? (
            <>
              <span className="text-paragraph-sm text-text-sub">{admin.email}</span>
              <span className="text-paragraph-xs text-text-soft">{STATUS_LABEL[admin.status] ?? admin.status}</span>
            </>
          ) : (
            <span className="text-paragraph-sm text-text-soft">
              This tenant has no administrator. Its memberships may have been removed.
            </span>
          )}
        </div>

        <Button variant="secondary" mode="outline" disabled={!admin} onClick={() => setOpen((value) => !value)}>
          {open ? 'Close' : 'Manage'}
        </Button>
      </div>

      {open && admin && (
        <div className="border-stroke-soft flex flex-col gap-5 border-t pt-4">
          <div className="flex flex-col gap-2">
            <span className="text-label-xs text-text-soft">Change sign-in address</span>
            <div className="flex flex-wrap gap-2">
              <Input
                type="email"
                value={newEmail}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewEmail(e.target.value)}
                placeholder={admin.email}
                className="min-w-[220px] flex-1"
              />
              <Button
                variant="primary"
                mode="filled"
                disabled={!newEmail || busy !== null}
                onClick={() =>
                  run('email', async () => {
                    const result = await changeTenantAdminEmail(organizationId, newEmail);
                    setNewEmail('');
                    setNotice(`Address changed to ${result.email}. All of their sessions were signed out.`);
                    onChanged();
                  })
                }
              >
                {busy === 'email' ? 'Changing...' : 'Change'}
              </Button>
            </div>
            <span className="text-paragraph-xs text-text-soft">
              Both the old and the new address are notified, and every existing session is signed out.
            </span>
          </div>

          <div className="flex flex-wrap gap-2">
            <Button
              variant="secondary"
              mode="outline"
              disabled={busy !== null}
              onClick={() =>
                run('reset', async () => {
                  await sendTenantAdminPasswordReset(organizationId);
                  setNotice('A password reset link has been sent.');
                })
              }
            >
              {busy === 'reset' ? 'Sending...' : 'Send password reset'}
            </Button>

            {admin.status !== 'active' && (
              <Button
                variant="secondary"
                mode="outline"
                disabled={busy !== null}
                onClick={() =>
                  run('invite', async () => {
                    const result = await resendTenantInvitation(organizationId);

                    if (result.invitationSent) {
                      setNotice('A fresh invitation has been sent.');
                    } else {
                      setFallbackLink(result.invitationLink ?? null);
                      setNotice('The email could not be sent. Deliver this link yourself.');
                    }

                    onChanged();
                  })
                }
              >
                {busy === 'invite' ? 'Sending...' : 'Resend invitation'}
              </Button>
            )}
          </div>

          {notice && <p className="text-paragraph-sm text-success-base">{notice}</p>}
          {error && <p className="text-paragraph-sm text-error-base">{error}</p>}

          {fallbackLink && (
            <div className="bg-bg-weak flex items-center gap-2 rounded-md p-2">
              <code className="text-paragraph-xs text-text-sub min-w-0 flex-1 break-all">{fallbackLink}</code>
              <CopyButton valueToCopy={fallbackLink} size="xs" ariaLabel="Copy invitation link" />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
